import { IIndicator, IInsight, IInsightDialog } from '@jumpstart/core';
import Alert from '@mui/material/Alert';
import { AlertColor } from '@mui/material/Alert/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import { SnackbarCloseReason } from '@mui/material/Snackbar/Snackbar';
import Typography from '@mui/material/Typography';
import LinkIcon from '@mui/icons-material/Link';
import React, { Fragment, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { auth } from '../../core/firebase';
import InsightNotificationsService from './insight-notifications-service';
import { InsightService } from './insight-service';
import { uiExternalUrls } from './states';
import DialogTree from './widgets/DialogTree';


export default function DialogPage() {
  const [user] = useAuthState(auth);
  const params = useParams<{ event: string; indicator: string }>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: AlertColor } | null>(null);
  const [event, setEvent] = useState<IInsight | null>(null);
  const [indicator, setIndicator] = useState<IIndicator | null>(null);

  const reload = () => {
    setLoading(true);
    setEvent(null);
    if (!params.event) {
      console.error('No event param')
      return;
    }
    InsightService.get(params.event)
      .then((e: IInsight | null) => {
        setLoading(false);
        setEvent(e);
        if (e) {
          const index: number = e.content.indicators.findIndex(i => i.id === params.indicator);
          if (index === -1) {
            setMessage({ text: `Failed to get indicator "${params.indicator}".`, severity: 'error' });
          } else {
            setIndicator(e.content.indicators[index]);
          }
        } else {
          setIndicator(null);
        }
      })
      .catch((err: Error) => {
        console.error(err);
        setMessage({ text: `Failed to get event "${params.event}". Error ${err.message}`, severity: 'error' });
        setLoading(false);
        setEvent(null);
        setIndicator(null);
      });
  }

  const handleCloseSnack = (event: React.SyntheticEvent<any> | Event, reason: SnackbarCloseReason) => {
    if (reason && reason === 'clickaway') {
      return;
    }
    setMessage(null);
  };

  const handleChangeDialog = (newIndicator: IIndicator, newDialog?: IInsightDialog) => {
    if (!event) {
      return;
    }
    const newEvent = {...event};
    console.log('Old', indicator);
    console.log('New', newIndicator);
    setIndicator(newIndicator);
    const index: number = newEvent.content.indicators.findIndex(i => i.id === newIndicator.id);
    if (index === -1) {
      throw new Error(`Could not find indicator "${newIndicator.id}"`);
    }
    newEvent.content.indicators[index] = newIndicator;
    setEvent(newEvent);
    setLoading(true);
    InsightService.updateContent({ id: newEvent.id, content: newEvent.content })
      .then(() => {
        setMessage({
          text: `Updated content to graph cms`,
          severity: 'success'
        });
        setLoading(false);
        if (!user) {
          return;
        }
        if (newDialog) {
          return InsightNotificationsService.dialog(newEvent, newIndicator, newDialog, user, 'updated');
        } else {
          return InsightNotificationsService.indicator(newEvent, newIndicator, user, 'updated');
        }
      })
      .catch((err: Error) => {
        setMessage({ text: err.message, severity: 'error' });
        console.error(err);
        setLoading(false);
      })
  }

  useEffect(() => {
    if (params.event) {
      reload();
    } else {
      setEvent(null);
    }
  }, [params.event, params.indicator]);

  return (
    <Grid container direction="column" style={{width: '100%', overflow: 'hidden', maxWidth: '100vw'}}>
      <Snackbar open={!!message} autoHideDuration={6000} onClose={handleCloseSnack}>
        <Alert onClose={() => setMessage(null)} severity={message ? message.severity : 'warning'}>
          {message ? message.text : ''}
        </Alert>
      </Snackbar>
      <Container maxWidth="xl" disableGutters>
      <Grid item>
        <Breadcrumbs>
          <Link color="inherit" to={`/admin/events/select`} component={RouterLink}>
            Events
          </Link>
          {event && <Link color="inherit" to={`/admin/events/event/${event.id}`} component={RouterLink}>{event.name}</Link>}
          {event && indicator && <Link color="inherit" to={`/admin/events/indicator/${event.id}/${indicator.id}`} component={RouterLink}>{indicator.name}</Link>}
          {event && indicator && (
            <Grid container direction="row" wrap="nowrap">
              <Typography color="textPrimary">Dialog</Typography>
              <Link
                href={uiExternalUrls.indicator(event, indicator)}
                target={`scout9-beta-mobile-${event.id}-${indicator.id}`}
                style={{height: 20, marginLeft: 8}}
              >
                <LinkIcon fontSize="small"/>
              </Link>
            </Grid>
          )}
        </Breadcrumbs>
      </Grid>
      </Container>
      <Grid item container direction="column">
        {event && indicator && <DialogTree
          indicator={indicator}
          event={event}
          loading={loading}
          onChange={handleChangeDialog}
        />}
      </Grid>
    </Grid>
  );
}
